import type { ReactNode } from "react";
import type { PageMetadata } from "~/app/api/pageData";
import { createPageSlotLookup } from "./PageSlotLookup";
import { getPageTemplate } from "./pageTemplateRegistry";
import { failUnknownTemplate, logSelectedTemplate, warnUnplacedSlots } from "./pageTemplateLogger";

interface PageTemplateRendererProps {
  metadata: PageMetadata;
  children: ReactNode;
}

export function PageTemplateRenderer({ metadata, children }: PageTemplateRendererProps) {
  const templateUid = metadata.templateUid;

  if (!templateUid) {
    throw new Error(
      `PageTemplateRenderer received an OK shell render with a missing/null metadata.templateUid` +
        ` (page uid "${
          metadata.uid ?? "<unknown>"
        }"). Every StorefrontShellFrame OK render must carry a non-null metadata.templateUid.`,
    );
  }

  const template = getPageTemplate(templateUid);

  if (!template) {
    failUnknownTemplate(templateUid);
    throw new Error(`No registered page template for templateUid "${templateUid}".`);
  }

  const selectedKey = template.name || templateUid;
  logSelectedTemplate(templateUid, selectedKey);

  const slots = createPageSlotLookup(metadata.slots, metadata, templateUid);
  const content = template({ metadata, slots, body: children });

  const consumed = new Set(slots.getConsumedPositions());
  const unplaced = metadata.slots
    .filter((slot) => slot.scope !== "global" && !consumed.has(slot.position))
    .map((slot) => slot.position);
  warnUnplacedSlots(templateUid, unplaced);

  return <>{content}</>;
}
